import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useRequestStore } from '../../store/requestStore';
import { RequestState } from '../../types';
import config from '../../config';
import { useTheme } from '../../theme';
import { AnimatedIcon } from '../../components/icons';
import { WEB_STATUS_COLORS } from '../../utils/statusPresentation';
import { toNumber } from '../../utils/number';
import { requestService } from '../../services/requestService';
import { proposalService } from '../../services/proposalService';
import { extractApiError } from '../../services/apiClient';

const STATUS_ORDER = [
  RequestState.REQUEST_SUBMITTED, 
  RequestState.UNDER_REVIEW, 
  RequestState.APPROVED, 
  RequestState.ASSIGNED, 
  RequestState.NEGOTIATING, 
  RequestState.CONFIRMED
];

const STATUS_COLORS = WEB_STATUS_COLORS;

export const RequestDetailScreen: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentRequest, fetchRequestDetail, updateRequestInList, isLoading } = useRequestStore();
  const { isDark } = useTheme();
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');

  useEffect(() => { 
    if (id) fetchRequestDetail(id); 
  }, [id]);

  useEffect(() => {
    if (!id) return;
    const pollId = setInterval(() => fetchRequestDetail(id), config.DEAL_POLL_INTERVAL);
    return () => clearInterval(pollId);
  }, [id]);

  if (isLoading || !currentRequest) {
    return (
      <div style={styles.loading}>
        <div style={styles.spinner} />
        <p style={{ color: 'var(--sc-text-secondary)' }}>Loading details...</p>
      </div>
    );
  }

  const req = currentRequest;
  const sc = STATUS_COLORS[req.status] || { bg: 'var(--sc-bg-input)', color: 'var(--sc-text-secondary)' };
  const currentIdx = STATUS_ORDER.indexOf(req.status);
  const isConfirmed = req.status === RequestState.CONFIRMED;
  const isClosed = req.status === RequestState.CLOSED;
  const hasSurveyor = req.assigned_surveyor != null;
  const isChatLocked = req.is_chat_blocked || req.status === RequestState.CLOSED;
  const canReview = req.status === RequestState.NEGOTIATING;
  const canResubmit = [RequestState.REJECTED, RequestState.CANCELLED].includes(req.status);
  const commissionRate = req.commission_rate ?? config.DEFAULT_COMMISSION_RATE;
  const dealId = req.deal_id || req.id;
  const pendingProposal = req.pending_proposal;
  const pendingSurveyor = pendingProposal?.surveyor_profile;

  const resubmitRequest = async () => {
    if (actionLoading) return;
    const requestId = req.id || id;
    if (!requestId) return;
    setActionLoading(true);
    setActionError('');
    try {
      const updated = await requestService.resubmit(requestId);
      updateRequestInList(updated);
      await fetchRequestDetail(requestId);
    } catch (err: any) {
      setActionError(extractApiError(err));
    } finally {
      setActionLoading(false);
    }
  };

  const handleOfferAction = async (action: 'accept' | 'reject') => {
    if (!pendingProposal || actionLoading) return;
    setActionLoading(true);
    setActionError('');
    try {
      if (action === 'accept') {
        const deal = await proposalService.accept(pendingProposal.id);
        updateRequestInList({ ...req, deal_id: deal.id, status: RequestState.ASSIGNED });
        await fetchRequestDetail(req.id);
        navigate(`/client/deal/${deal.id}`);
      } else {
        const confirmed = window.confirm('Decline this offer and reopen the job for surveyors?');
        if (!confirmed) return;
        const updatedProposal = await proposalService.reject(pendingProposal.id);
        await fetchRequestDetail(updatedProposal.survey_request || req.id);
      }
    } catch (err: any) {
      setActionError(extractApiError(err));
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <button onClick={() => navigate(-1)} style={styles.backBtn} className="sc-press">
        ← Back to Requests
      </button>

      {/* Header */}
      <div style={styles.headerRow}>
        <div>
          <h1 style={styles.title}>{req.service_type}</h1>
          <p style={styles.subtitle}>Request #{req.id.substring(0, 8).toUpperCase()}</p>
        </div>
        <span style={{ ...styles.statusBadge, background: sc.bg, color: sc.color }}>
          {req.status.replace(/_/g, ' ')}
        </span>
      </div>

      {/* Horizontal timeline step indicator */}
      <div style={styles.timeline} className="sc-animate-fade-in">
        {STATUS_ORDER.map((step, i) => {
          const done = i <= currentIdx && !isClosed;
          const active = i === currentIdx && !isClosed;
          return (
            <div key={step} style={styles.timeStep}>
              <div style={{
                ...styles.dot, 
                background: done ? 'var(--sc-brand-primary)' : 'var(--sc-bg-input)', 
                border: active ? '3px solid var(--sc-brand-primary-light)' : '1.5px solid var(--sc-border)',
                width: active ? 22 : 16, 
                height: active ? 22 : 16 
              }}>
                {done && !active && <span style={{ color: '#FFF', fontSize: 9 }}>✓</span>}
              </div>
              {i < STATUS_ORDER.length - 1 && (
                <div style={{
                  ...styles.line, 
                  background: done ? 'var(--sc-brand-primary)' : 'var(--sc-border)' 
                }} />
              )}
              <div style={{
                ...styles.stepLabel,
                color: active ? 'var(--sc-brand-primary)' : 'var(--sc-text-secondary)',
                fontWeight: active ? 700 : 500
              }}>{step.replace(/_/g, ' ')}</div>
            </div>
          );
        })}
        {(isClosed || req.status === RequestState.DISPUTED) && (
          <div style={styles.timeStep}>
            <div style={{ ...styles.dot, background: 'var(--sc-error)', width: 18, height: 18, color: '#FFF' }}>✕</div>
            <div style={styles.stepLabel}>{req.status.replace(/_/g, ' ')}</div>
          </div>
        )}
      </div>

      <div style={styles.grid}>
        {/* Request details info card */}
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>Request Details</h3>
          <div style={styles.field}>
            <span style={styles.fieldLabel}>Service Type</span>
            <span style={styles.fieldValue}>{req.service_type}</span>
          </div>
          <div style={styles.field}>
            <span style={styles.fieldLabel}>Description</span>
            <span style={{ ...styles.fieldValue, lineHeight: 1.5 }}>{req.description}</span>
          </div>
          <div style={styles.field}>
            <span style={styles.fieldLabel}>Site Location</span>
            <span style={styles.fieldValue}>📍 {req.address}</span>
          </div>
          <div style={styles.field}>
            <span style={styles.fieldLabel}>Exact Coordinates</span>
            <span style={styles.fieldValue}>{req.latitude}, {req.longitude}</span>
          </div>
          {req.deadline && (
            <div style={styles.field}>
              <span style={styles.fieldLabel}>Deadline</span>
              <span style={styles.fieldValue}>{new Date(req.deadline).toLocaleDateString()}</span>
            </div>
          )}
          <div style={styles.field}>
            <span style={styles.fieldLabel}>Submitted On</span>
            <span style={styles.fieldValue}>{new Date(req.created_at).toLocaleDateString()}</span>
          </div>
        </div>

        {/* Action Panel */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {req.assigned_surveyor && (
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Assigned Surveyor</h3>
              <div style={styles.surveyorRow}>
                <div style={styles.surveyorAvatar}>
                  {(req.assigned_surveyor?.first_name?.[0] || 'S').toUpperCase()}
                </div>
                <div>
                  <div style={{ fontWeight: 700, color: 'var(--sc-text-primary)' }}>
                    {req.assigned_surveyor.first_name} {req.assigned_surveyor.last_name}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--sc-text-secondary)', marginTop: 4 }}>
                    ⭐ {toNumber(req.assigned_surveyor.rating_average).toFixed(1)} average rating · {req.assigned_surveyor.total_jobs} deals completed
                  </div>
                </div>
              </div>
              
              {isConfirmed ? (
                <div style={styles.contactBox}>
                  <h4 style={{ fontSize: 13, fontWeight: 700, margin: '0 0 6px', color: 'var(--sc-success-text)' }}>📞 Contact Shared</h4>
                  <p style={{ fontSize: 13, margin: '2px 0', color: 'var(--sc-success-text)' }}>Phone: {req.assigned_surveyor.phone || 'N/A'}</p>
                  <p style={{ fontSize: 13, margin: '2px 0', color: 'var(--sc-success-text)' }}>Email: {req.assigned_surveyor.email || 'N/A'}</p>
                </div>
              ) : (
                <div style={styles.warningBanner}>
                  ⚠️ Contact info is locked until the deal is confirmed.
                </div>
              )}
            </div>
          )}

          {pendingProposal && (
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Surveyor Offer</h3>
              <div style={styles.offerSurveyor}>
                {pendingSurveyor?.full_name ||
                  `${pendingSurveyor?.first_name || ''} ${pendingSurveyor?.last_name || ''}`.trim() ||
                  pendingProposal.surveyor_email ||
                  'Verified surveyor'}
              </div>
              <div style={styles.offerPrice}>${Number(pendingProposal.proposed_price || 0).toLocaleString()}</div>
              {pendingProposal.message && <p style={styles.offerMessage}>{pendingProposal.message}</p>}
              <div style={styles.offerActions}>
                <button
                  type="button"
                  onClick={() => handleOfferAction('accept')}
                  disabled={actionLoading}
                  style={{ ...styles.actionBtn, border: 'none', flex: 1 }}
                  className="sc-press"
                >
                  {actionLoading ? 'Working...' : 'Accept Offer'}
                </button>
                <button
                  type="button"
                  onClick={() => handleOfferAction('reject')}
                  disabled={actionLoading}
                  style={{ ...styles.actionBtn, background: 'var(--sc-bg-input)', color: 'var(--sc-text-primary)', border: '1px solid var(--sc-border)', boxShadow: 'none', flex: 1 }}
                  className="sc-press"
                >
                  Decline
                </button>
              </div>
            </div>
          )}

          <div style={styles.card}>
            <h3 style={styles.cardTitle}>Operations</h3>
            <div style={styles.actionsCol}>
              {actionError && (
                <div style={styles.errorBox}>{actionError}</div>
              )}
              {hasSurveyor && (
                <Link to={`/client/chat/${dealId}`} style={{
                  ...styles.actionBtn,
                  background: isChatLocked ? 'var(--sc-bg-input)' : 'var(--sc-brand-gradient)',
                  color: isChatLocked ? 'var(--sc-text-tertiary)' : '#FFF',
                  border: isChatLocked ? '1px solid var(--sc-border)' : 'none',
                  boxShadow: isChatLocked ? 'none' : 'var(--sc-shadow-brand)',
                }} className="sc-press">
                  💬 {isChatLocked ? 'Chat (Locked)' : 'Message Surveyor'}
                </Link>
              )}
              {canReview && !pendingProposal && (
                <Link to={`/client/deal/${dealId}`} style={{ ...styles.actionBtn, background: 'var(--sc-warning)' }} className="sc-press">
                  🤝 Review Proposal Deal
                </Link>
              )}
              {isConfirmed && (
                <Link to={`/client/rate/${dealId}`} style={{ ...styles.actionBtn, background: '#8B5CF6' }} className="sc-press">
                  ⭐ Leave Surveyor Rating
                </Link>
              )}
              {canResubmit && (
                <button
                  type="button"
                  onClick={resubmitRequest}
                  disabled={actionLoading}
                  style={{ ...styles.actionBtn, background: 'var(--sc-brand-primary)', border: 'none' }}
                  className="sc-press"
                >
                  {actionLoading ? 'Resubmitting...' : 'Resubmit Request'}
                </button>
              )}
              {!hasSurveyor && !canReview && !isConfirmed && !canResubmit && (
                <p style={{ fontSize: 13, color: 'var(--sc-text-secondary)', textAlign: 'center', margin: '8px 0' }}>
                  Awaiting review. No operations available at this stage.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Financial confirmation summary card */}
      {isConfirmed && (
        <div style={styles.paymentCard} className="sc-animate-scale-in">
          <h3 style={styles.paymentTitle}>
            ✅ Secured Payment Summary
          </h3>
          <div style={styles.financialGrid}>
            <div style={styles.finBox}>
              <div style={styles.finLabel}>Agreed Deal Value</div>
              <div style={styles.finVal}>{req.deal_value != null ? `$${req.deal_value.toLocaleString()}` : 'Pending'}</div>
            </div>
            <div style={styles.finBox}>
              <div style={styles.finLabel}>Commission Rate</div>
              <div style={styles.finVal}>{(commissionRate * 100).toFixed(0)}%</div>
            </div>
            <div style={styles.finBox}>
              <div style={styles.finLabel}>Expected Commission</div>
              <div style={styles.finVal}>
                {req.expected_commission != null
                  ? `$${req.expected_commission.toFixed(2)}`
                  : req.deal_value != null
                    ? `$${(req.deal_value * commissionRate).toFixed(2)}`
                    : '—'}
              </div>
            </div>
          </div>
          <p style={{ fontSize: 12, color: 'var(--sc-success-text)', margin: '14px 0 0', lineHeight: 1.5 }}>
            ℹ Platform commission is automatically factored in. External payments and surveyor disbursements are processed in compliance with service delivery terms.
          </p>
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: { maxWidth: 860, margin: '0 auto', paddingBottom: 40, fontFamily: 'var(--sc-font-family)' },
  loading: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 60 },
  spinner: { width: 32, height: 32, border: '3px solid var(--sc-border)', borderTopColor: 'var(--sc-brand-primary)', borderRadius: '50%', animation: 'sc-spin 0.8s linear infinite', marginBottom: 12 },
  backBtn: { background: 'none', border: 'none', color: 'var(--sc-brand-primary)', fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: 0, marginBottom: 16 },

  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, gap: 16 },
  title: { fontSize: 24, fontWeight: 800, color: 'var(--sc-text-primary)', margin: '0 0 4px', fontFamily: 'var(--sc-font-display)' },
  subtitle: { fontSize: 13, color: 'var(--sc-text-tertiary)', margin: 0, fontWeight: 600 },
  statusBadge: { padding: '6px 14px', borderRadius: 20, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', border: '1px solid currentColor' },

  timeline: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 28, background: 'var(--sc-bg-secondary)', borderRadius: 'var(--sc-radius-lg)', padding: '24px 20px', border: '1px solid var(--sc-border)', boxShadow: 'var(--sc-shadow-card)', position: 'relative', overflowX: 'auto', WebkitOverflowScrolling: 'touch' },
  timeStep: { display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, width: 84, gap: 8, position: 'relative', zIndex: 2 },
  dot: { borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1, transition: 'all var(--sc-transition-fast)' },
  line: { position: 'absolute', top: 10, left: '50%', width: '100%', height: 2, zIndex: 0 },
  stepLabel: { fontSize: 10, textAlign: 'center', textTransform: 'capitalize', marginTop: 4, maxWidth: 84 },

  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 20 },
  card: { background: 'var(--sc-bg-secondary)', borderRadius: 'var(--sc-radius-lg)', padding: 24, border: '1px solid var(--sc-border)', boxShadow: 'var(--sc-shadow-card)' },
  cardTitle: { fontSize: 15, fontWeight: 700, color: 'var(--sc-text-primary)', margin: '0 0 16px', paddingBottom: 12, borderBottom: '1px solid var(--sc-border-light)', fontFamily: 'var(--sc-font-display)' },
  
  field: { display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 14 },
  fieldLabel: { fontSize: 10, fontWeight: 700, color: 'var(--sc-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.5px' },
  fieldValue: { fontSize: 14, color: 'var(--sc-text-primary)', fontWeight: 500 },

  surveyorRow: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 },
  surveyorAvatar: { width: 44, height: 44, borderRadius: '50%', background: 'var(--sc-brand-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, color: '#FFF' },
  contactBox: { background: 'var(--sc-success-bg)', borderRadius: 'var(--sc-radius-md)', padding: 14, border: '1px solid var(--sc-success)' },
  warningBanner: { background: 'var(--sc-warning-bg)', borderRadius: 'var(--sc-radius-md)', padding: '10px 14px', fontSize: 12.5, color: 'var(--sc-warning-text)', fontWeight: 600, border: '1px solid var(--sc-warning)' },
  offerSurveyor: { fontSize: 13, fontWeight: 700, color: 'var(--sc-text-primary)', marginBottom: 6 },
  offerPrice: { fontSize: 24, fontWeight: 800, color: 'var(--sc-brand-primary)', marginBottom: 10 },
  offerMessage: { fontSize: 13, lineHeight: 1.5, color: 'var(--sc-text-secondary)', margin: '0 0 14px' },
  offerActions: { display: 'flex', gap: 10, flexWrap: 'wrap' },

  actionsCol: { display: 'flex', flexDirection: 'column', gap: 10 },
  errorBox: { background: 'var(--sc-error-bg)', border: '1px solid var(--sc-error)', borderRadius: 'var(--sc-radius-md)', color: 'var(--sc-error-text)', fontSize: 13, lineHeight: 1.5, padding: '10px 12px' },
  actionBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: 'var(--sc-touch-btn)', background: 'var(--sc-brand-gradient)', color: '#FFF', borderRadius: 'var(--sc-radius-md)', fontSize: 14, fontWeight: 700, textDecoration: 'none', cursor: 'pointer', boxShadow: 'var(--sc-shadow-brand)', transition: 'all var(--sc-transition-fast)' },

  paymentCard: { background: 'var(--sc-success-bg)', border: '1px solid var(--sc-success)', borderRadius: 'var(--sc-radius-lg)', padding: 24, marginTop: 20 },
  paymentTitle: { fontSize: 15, fontWeight: 700, color: 'var(--sc-success-text)', margin: '0 0 16px', borderBottom: '1px solid rgba(16,185,129,0.2)', paddingBottom: 12, fontFamily: 'var(--sc-font-display)' },
  financialGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: 12 },
  finBox: { background: 'var(--sc-bg-secondary)', borderRadius: 'var(--sc-radius-md)', padding: '14px 10px', textAlign: 'center', border: '1px solid var(--sc-border-light)' },
  finLabel: { fontSize: 9, fontWeight: 700, color: 'var(--sc-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: 4 },
  finVal: { fontSize: 18, fontWeight: 800, color: 'var(--sc-text-primary)' },
};
